import React, { useCallback, useContext } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import styles from "./NetAmountField.module.css";

interface INetAmountFieldProps {
    accountingRowIndex: number;
}

const NetAmountFieldBase = (props: INetAmountFieldProps) => {
    const { t } = useTranslation();
    const { invoiceStore } = useContext(RootStoreContext);
    const { accountingRowIndex } = props;

    const displayedNetAmount = invoiceStore.currentInvoice?.displayedNetAmounts[accountingRowIndex] || "";

    const onChangeNetAmountInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            const cursorPosition = e.target.selectionStart;

            // Remove apostrophes for processing
            const cleanValue = newValue.replace(/'/g, "");
            const numericValue = parseFloat(cleanValue);

            // Update displayed net amount in store
            invoiceStore.currentInvoice?.setDisplayedNetAmount(newValue, accountingRowIndex);

            // Update store with clean numeric value, but don't update accounting amount
            if (!isNaN(numericValue)) {
                invoiceStore.updateAccountingWithNetAmount(String(numericValue), accountingRowIndex);
            }

            // Restore cursor position after React re-renders
            setTimeout(() => {
                if (e.target.setSelectionRange && cursorPosition !== null) {
                    e.target.setSelectionRange(cursorPosition, cursorPosition);
                }
            }, 0);
        },
        [invoiceStore, accountingRowIndex]
    );

    const onFocusNetAmountInput = useCallback(() => {
        invoiceStore.currentInvoice?.setNetAmountFieldFocused(true, accountingRowIndex);
    }, [invoiceStore, accountingRowIndex]);

    const onBlurNetAmountInput = useCallback(() => {
        invoiceStore.currentInvoice?.setNetAmountFieldFocused(false, accountingRowIndex);
    }, [invoiceStore, accountingRowIndex]);

    return (
        <UI.Input
            label={`${t("screens.kredi_flow.accountings.amount")} ${t("screens.kredi_flow.accountings.excl_vat")}`}
            className={styles.AccountingAmountInput}
            type="text"
            autoComplete="off"
            value={displayedNetAmount}
            onChange={onChangeNetAmountInput}
            onFocus={onFocusNetAmountInput}
            onBlur={onBlurNetAmountInput}
        />
    );
};

export const NetAmountField = Sentry.withProfiler(observer(NetAmountFieldBase));
